import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Star, MapPin, Phone, Mail, Calendar, Users, Sparkles, Leaf, Heart, Shield, Award, ChevronRight, Quote } from 'lucide-react';
import { WELLNESS_API, fmtUSD } from './_shared';

// Authentic Ayurvedic centre branding
const heroBgImage = '/drraju-hero.jpg';
const vaidyas = [
  { name: 'Vaidya B.A. Raju', role: 'Family Patriarch', initials: 'BA' },
  { name: 'Vaidya J. R. Raju', role: 'Senior Physician', initials: 'JR' },
  { name: 'Vaidya A.V. "Krishna" Raju', role: 'Chief Vaidya', initials: 'KR' },
  { name: 'Vaidya Padma Nayani Raju', role: 'Senior Vaidya', initials: 'PR' },
];

export default function WellnessLanding() {
  const nav = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [reviewsData, setReviewsData] = useState({ reviews: [], average_rating: 0, total_reviews: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [r, t, rv] = await Promise.all([
          axios.get(`${WELLNESS_API}/rooms`),
          axios.get(`${WELLNESS_API}/treatments`),
          axios.get(`${WELLNESS_API}/reviews`),
        ]);
        setRooms(r.data || []);
        setTreatments(t.data || []);
        setReviewsData(rv.data || { reviews: [] });
      } catch { /* noop */ }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#faf7ef] text-[#2a2620]" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }} data-testid="wellness-landing">
      <WellnessNav />

      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroBgImage} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2f5d50]/85 via-[#2f5d50]/60 to-transparent" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-20 text-white">
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#e8c373] mb-3 font-sans font-semibold">
              <Leaf className="inline w-3.5 h-3.5 mr-1" />
              Authentic Maharishi Ayurveda · Family Heritage
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-tight mb-3">
              Dr. Raju&apos;s Institute
              <br />
              <span className="text-[#e8c373]">of Ayurveda</span>
            </h1>
            <p className="text-xl text-[#e8c373] italic mb-5">Restoring the Memory of Perfect Health</p>
            <p className="text-base sm:text-lg text-white/85 mb-3 font-sans" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
              Hyderabad, Telangana · Healing patients worldwide for generations
            </p>
            <p className="text-white/80 mb-8 leading-relaxed font-sans max-w-xl" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
              A heritage of distinguished Vaidyas guided by Maharishi Mahesh
              Yogi&apos;s vision. Pulse Diagnosis (Nadi Pariksha), Panchakarma
              Rejuvenation, the signature Raju Banana program, and the proprietary
              Smriti formula — all delivered in a traditional family atmosphere.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => nav('/wellness/book')}
                className="px-7 py-3.5 rounded-full bg-[#c89b3c] hover:bg-[#b58931] text-white text-sm font-semibold shadow-lg transition flex items-center gap-2 font-sans"
                data-testid="hero-book-now"
              >
                <Calendar className="w-4 h-4" />
                Book Your Stay
              </button>
              <button
                onClick={() => nav('/wellness/rooms')}
                className="px-7 py-3.5 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-white text-sm font-semibold border border-white/30 transition font-sans"
                data-testid="hero-view-rooms"
              >
                View Rooms & Programs
              </button>
            </div>
            <div className="mt-10 flex flex-wrap gap-8 text-sm font-sans" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
              <Stat icon={Star} value={reviewsData.average_rating || '4.8'} label="Guest rating" />
              <Stat icon={Users} value={reviewsData.total_reviews || '247'} label="Healed last year" />
              <Stat icon={Award} value="Generations" label="Of family heritage" />
            </div>
          </div>
        </div>
      </section>

      {/* HERITAGE — THE RAJU FAMILY */}
      <Section bg="#f3ece0">
        <SectionHeader eyebrow="The Raju Family" title="A heritage of healing for generations" />
        <p className="text-center text-[#6b6358] font-sans max-w-3xl mx-auto -mt-2 mb-12 text-sm leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
          The Raju Family comes from a long line of distinguished Vaidyas (Ayurvedic Physicians)
          going back for generations on both the mother&apos;s and father&apos;s sides. Native to Andhra Pradesh,
          they have practiced authentic natural healthcare through Ayurveda in Hyderabad — at Champapet and
          Sahara States — guided by His Holiness Maharishi Mahesh Yogi&apos;s vision of preserving
          this ancient knowledge in its perfect, authentic form.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {vaidyas.map((v) => (
            <VaidyaCard key={v.initials} name={v.name} role={v.role} initials={v.initials} />
          ))}
        </div>
      </Section>

      {/* PILLARS */}
      <Section bg="#faf7ef">
        <SectionHeader
          eyebrow="Why Dr. Raju's Centre"
          title="Authentic. Personal. Transformative."
        />
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          <Pillar icon={Leaf} title="Pulse Diagnosis (Nadi Pariksha)" body="The foundation of all our treatments. The Vaidyas read your pulse to reveal imbalances 25 years deep — and design a precise treatment plan." />
          <Pillar icon={Heart} title="Maharishi Ayurveda Tradition" body="Preserved exactly as Maharishi Mahesh Yogi taught — authentic, complete, and effortless. No modern shortcuts." />
          <Pillar icon={Shield} title="Family-Run, Traditional" body="Multi-generational care from the same family of Vaidyas. Returning guests come for 10+ years." />
        </div>
      </Section>

      {/* ROOMS PREVIEW */}
      <Section bg="#faf7ef">
        <SectionHeader eyebrow="Your Stay" title="Choose your room category" />
        <p className="text-center text-[#6b6358] font-sans max-w-2xl mx-auto -mt-2 mb-10 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
          All rooms include 3 Sattvic vegetarian meals daily, herbal teas, free Wi-Fi,
          daily yoga & meditation, and full access to the herbal garden.
        </p>
        <div className="grid md:grid-cols-3 gap-6">
          {rooms.map((r) => (
            <RoomPreviewCard key={r.id} room={r} onBook={() => nav(`/wellness/book?room=${r.id}`)} />
          ))}
        </div>
      </Section>

      {/* TREATMENTS */}
      <Section bg="#f3ece0">
        <SectionHeader eyebrow="Healing Programs" title="Treatments tailored to you" />
        <p className="text-center text-[#6b6358] font-sans max-w-2xl mx-auto -mt-2 mb-10 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
          Add any treatment to your stay. Doctors finalise the plan after your arrival
          consultation. Pricing is per day of active treatment.
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {treatments.map((t) => <TreatmentCard key={t.id} t={t} />)}
        </div>
      </Section>

      {/* TESTIMONIALS */}
      <Section bg="#2f5d50" textColor="#faf7ef">
        <SectionHeader
          eyebrow="Guest stories"
          title="From healing to lifelong transformation"
          dark
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {reviewsData.reviews.slice(0, 6).map((r) => <Testimonial key={r.id} r={r} />)}
        </div>
      </Section>

      {/* CONTACT */}
      <Section bg="#faf7ef">
        <SectionHeader eyebrow="Visit us" title="Begin your journey" />
        <div className="grid md:grid-cols-3 gap-6 mt-10 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
          <ContactCard icon={MapPin} title="Address" lines={["Dr. Raju's Institute of Ayurveda", '8/19 Bahar-A, Sahara States', 'Mansoorabad, L.B. Nagar', 'Hyderabad 500068, Telangana, India']} />
          <ContactCard icon={Phone} title="Connect with us" lines={['Instagram: @drraju108', 'Facebook: /ayurveda.raju', 'YouTube: Dr. Raju Ayurveda']} />
          <ContactCard icon={Mail} title="Book a consultation" lines={['Online video consultation with the Vaidyas', 'Schedule via our booking system', 'Pre-arrival or stand-alone sessions']} />
        </div>
        <div className="mt-12 text-center">
          <button
            onClick={() => nav('/wellness/book')}
            className="px-8 py-4 rounded-full bg-[#2f5d50] hover:bg-[#244a40] text-white font-semibold shadow-lg transition inline-flex items-center gap-2 font-sans"
            data-testid="bottom-cta-book"
          >
            Book Your Stay
            <ChevronRight className="w-4 h-4" />
          </button>
          <p className="mt-3 text-xs text-[#6b6358] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
            Pay at arrival · Free cancellation up to 7 days before check-in
          </p>
        </div>
      </Section>

      <WellnessFooter />
    </div>
  );
}

// ---------- Components ----------

export function WellnessNav() {
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-40 bg-[#faf7ef]/90 backdrop-blur-xl border-b border-[#e0d6c2]">
      <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
        <button onClick={() => nav('/wellness')} className="flex items-center gap-3" data-testid="wellness-logo">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2f5d50] to-[#5b8b7e] flex items-center justify-center text-white">
            <Leaf className="w-4 h-4" />
          </div>
          <div className="leading-tight text-left">
            <p className="text-[15px] font-bold text-[#2a2620]">Dr. Raju Ayurveda</p>
            <p className="text-[10px] text-[#6b6358] uppercase tracking-widest font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>Institute · Hyderabad</p>
          </div>
        </button>
        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-[#2a2620] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
          <button onClick={() => nav('/wellness/rooms')} className="hover:text-[#2f5d50] transition" data-testid="nav-rooms">Rooms</button>
          <button onClick={() => nav('/wellness/treatments')} className="hover:text-[#2f5d50] transition" data-testid="nav-treatments">Treatments</button>
          <button onClick={() => nav('/wellness/lookup')} className="hover:text-[#2f5d50] transition" data-testid="nav-lookup">My Booking</button>
        </nav>
        <button
          onClick={() => nav('/wellness/book')}
          className="px-5 py-2 rounded-full bg-[#c89b3c] hover:bg-[#b58931] text-white text-xs font-semibold transition font-sans"
          data-testid="nav-book-now"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          Book Now
        </button>
      </div>
    </header>
  );
}

export function WellnessFooter() {
  return (
    <footer className="bg-[#1f3d34] text-[#d6c9a8] py-10 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="max-w-6xl mx-auto px-6 text-center">
        <p className="text-sm">© {new Date().getFullYear()} Dr. Raju&apos;s Institute of Ayurveda, Hyderabad. All rights reserved.</p>
        <p className="text-[11px] mt-1 opacity-70 italic">Restoring the Memory of Perfect Health</p>
      </div>
    </footer>
  );
}

function Section({ children, bg, textColor }) {
  return (
    <section style={{ backgroundColor: bg, color: textColor || undefined }} className="py-16 sm:py-20">
      <div className="max-w-6xl mx-auto px-6">{children}</div>
    </section>
  );
}

function SectionHeader({ eyebrow, title, dark }) {
  return (
    <div className="text-center">
      <p className={`text-[10px] uppercase tracking-[0.3em] font-semibold font-sans ${dark ? 'text-[#e8c373]' : 'text-[#c89b3c]'}`} style={{ fontFamily: 'Inter, sans-serif' }}>{eyebrow}</p>
      <h2 className="mt-2 text-3xl sm:text-4xl font-bold">{title}</h2>
    </div>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-[#e8c373]" />
      <div>
        <div className="font-bold leading-none">{value}</div>
        <div className="text-[10px] text-white/70 uppercase tracking-widest">{label}</div>
      </div>
    </div>
  );
}

function Pillar({ icon: Icon, title, body }) {
  return (
    <div className="bg-white rounded-2xl p-7 border border-[#e0d6c2] shadow-sm">
      <div className="w-12 h-12 rounded-full bg-[#2f5d50]/10 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-[#2f5d50]" />
      </div>
      <h3 className="text-lg font-bold mb-2">{title}</h3>
      <p className="text-sm text-[#6b6358] font-sans leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>{body}</p>
    </div>
  );
}

function VaidyaCard({ name, role, initials }) {
  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-[#e0d6c2] shadow-sm text-center hover:shadow-lg transition">
      <div
        className="aspect-square relative overflow-hidden flex items-center justify-center"
        style={{
          background:
            'radial-gradient(circle at 30% 30%, #5b8b7e 0%, #2f5d50 60%, #1f3d34 100%)',
        }}
      >
        {/* Mandala / ornament backdrop */}
        <svg
          viewBox="0 0 200 200"
          className="absolute inset-0 w-full h-full opacity-20"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <defs>
            <radialGradient id="g1" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#e8c373" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#e8c373" stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle cx="100" cy="100" r="90" fill="none" stroke="#e8c373" strokeWidth="0.5" />
          <circle cx="100" cy="100" r="70" fill="none" stroke="#e8c373" strokeWidth="0.4" />
          <circle cx="100" cy="100" r="50" fill="url(#g1)" />
          {[...Array(12)].map((_, i) => (
            <line
              key={i}
              x1="100"
              y1="100"
              x2={100 + 90 * Math.cos((i * Math.PI) / 6)}
              y2={100 + 90 * Math.sin((i * Math.PI) / 6)}
              stroke="#e8c373"
              strokeWidth="0.3"
            />
          ))}
        </svg>
        {/* Lotus icon top */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 text-[#e8c373] opacity-80">
          <Leaf className="w-5 h-5" />
        </div>
        {/* Initials */}
        <div className="relative z-10 text-center">
          <div
            className="text-6xl font-bold text-[#e8c373] tracking-wider drop-shadow-lg"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            {initials}
          </div>
          <div className="mt-2 text-[9px] uppercase tracking-[0.3em] text-[#faf7ef]/70 font-semibold"
               style={{ fontFamily: 'Inter, sans-serif' }}>
            Vaidya
          </div>
        </div>
        {/* Bottom ornament */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
          <span className="w-1 h-1 rounded-full bg-[#e8c373]/60" />
          <span className="w-1.5 h-1.5 rounded-full bg-[#e8c373]" />
          <span className="w-1 h-1 rounded-full bg-[#e8c373]/60" />
        </div>
      </div>
      <div className="p-4">
        <h4 className="text-sm font-bold leading-tight">{name}</h4>
        <p
          className="text-[10px] uppercase tracking-widest text-[#c89b3c] mt-1 font-sans font-semibold"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {role}
        </p>
      </div>
    </div>
  );
}

function RoomPreviewCard({ room, onBook }) {
  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-[#e0d6c2] shadow-sm hover:shadow-xl transition group" data-testid={`room-card-${room.id}`}>
      <div className="aspect-[4/3] overflow-hidden">
        <img src={room.image_url} alt={room.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
      </div>
      <div className="p-6">
        <h3 className="text-xl font-bold">{room.name}</h3>
        <p className="text-xs text-[#6b6358] mt-1 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>{room.subtitle}</p>
        <div className="mt-3 flex items-baseline gap-1">
          <span className="text-3xl font-bold text-[#2f5d50]">{fmtUSD(room.price_per_night)}</span>
          <span className="text-xs text-[#6b6358] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>/ night</span>
        </div>
        <p className="text-[11px] text-[#6b6358] mt-2 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
          {room.bed} · {room.size_sqft} sq ft · {room.view}
        </p>
        <ul className="mt-4 space-y-1.5 text-xs text-[#2a2620] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
          {room.amenities.slice(0, 4).map((a, i) => (
            <li key={i} className="flex items-center gap-2">
              <Sparkles className="w-3 h-3 text-[#c89b3c]" />{a}
            </li>
          ))}
        </ul>
        <button
          onClick={onBook}
          className="mt-5 w-full py-2.5 rounded-full bg-[#2f5d50] hover:bg-[#244a40] text-white text-sm font-semibold transition font-sans"
          style={{ fontFamily: 'Inter, sans-serif' }}
          data-testid={`room-book-${room.id}`}
        >
          Book this room
        </button>
      </div>
    </div>
  );
}

function TreatmentCard({ t }) {
  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-[#e0d6c2] shadow-sm" data-testid={`treatment-card-${t.id}`}>
      <div className="aspect-[16/9] overflow-hidden">
        <img src={t.image_url} alt={t.name} className="w-full h-full object-cover" />
      </div>
      <div className="p-5">
        <p className="text-[10px] uppercase tracking-widest text-[#c89b3c] font-semibold font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>{t.category}</p>
        <h3 className="text-base font-bold mt-1">{t.name}</h3>
        <p className="text-xs text-[#6b6358] mt-1.5 leading-relaxed font-sans line-clamp-3" style={{ fontFamily: 'Inter, sans-serif' }}>{t.description}</p>
        <div className="mt-3 flex items-center justify-between text-xs font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
          <span className="text-[#2f5d50] font-semibold">{fmtUSD(t.price_per_day)}/day</span>
          <span className="text-[#6b6358]">{t.min_days}–{t.duration_days} days</span>
        </div>
      </div>
    </div>
  );
}

function Testimonial({ r }) {
  return (
    <div className="bg-[#faf7ef]/10 backdrop-blur rounded-2xl p-6 border border-white/10">
      <Quote className="w-5 h-5 text-[#e8c373] mb-3" />
      <p className="text-sm leading-relaxed mb-4 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>&ldquo;{r.body}&rdquo;</p>
      <div className="text-xs opacity-80 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
        <p className="font-semibold text-[#e8c373]">{r.name} · {r.country}</p>
        <p className="opacity-80 mt-0.5">{r.program} · {r.stay_days} days</p>
      </div>
    </div>
  );
}

function ContactCard({ icon: Icon, title, lines }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-[#e0d6c2] shadow-sm">
      <Icon className="w-5 h-5 text-[#2f5d50] mb-3" />
      <h4 className="text-sm font-bold mb-2 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>{title}</h4>
      {lines.map((l, i) => <p key={i} className="text-xs text-[#6b6358] leading-relaxed">{l}</p>)}
    </div>
  );
}
